import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TRAINING_NAME = 'The 7-Day New Model of NLP and Classical NLP Practitioner Certification';
const DEFAULT_SECONDS = 15 * 60;

const PRESETS = [
  { label: '5 min', seconds: 5 * 60 },
  { label: '10 min', seconds: 10 * 60 },
  { label: '15 min', seconds: 15 * 60 },
  { label: '20 min', seconds: 20 * 60 },
  { label: '30 min', seconds: 30 * 60 },
];

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function TrainingTimer() {
  const [total, setTotal] = useState(DEFAULT_SECONDS);
  const [remaining, setRemaining] = useState(DEFAULT_SECONDS);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (running && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current!);
            setRunning(false);
            setFinished(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [running]);

  const handlePreset = (seconds: number) => {
    if (running) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    setTotal(seconds);
    setRemaining(seconds);
    setFinished(false);
  };

  const handleReset = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRunning(false);
    setFinished(false);
    setRemaining(total);
  };

  const progress = remaining / total;
  const radius = 130;
  const circumference = 2 * Math.PI * radius;
  const isLow = remaining <= 60 && remaining > 0;
  const isCritical = remaining <= 10 && remaining > 0;

  const ringColor = isCritical ? '#e74c3c' : isLow ? '#e67e22' : '#6B9B5A';
  const glowColor = isCritical ? 'rgba(231,76,60,0.25)' : isLow ? 'rgba(230,126,34,0.2)' : 'rgba(107,155,90,0.15)';

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-chocolate-900 via-chocolate-800 to-chocolate-900 relative overflow-hidden" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Ambient glow orbs */}
      <motion.div
        className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(107,155,90,0.12) 0%, transparent 70%)' }}
        animate={{ x: [0, 40, 0], y: [0, 30, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-10%] right-[-5%] w-[400px] h-[400px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(201,169,97,0.1) 0%, transparent 70%)' }}
        animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-8 pb-6">
        <img
          src="/nlp_os_logo_off_white_creme_bg_.png"
          alt="NLP OS"
          className="h-10 md:h-12 object-contain object-left"
        />
        <div className="hidden md:flex items-center gap-2 px-4 py-1.5 rounded-full border border-cream-200/20 bg-white/5 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-sage-400 animate-pulse" />
          <span className="text-xs tracking-[0.2em] uppercase text-cream-200/70 font-medium">Training Timer</span>
        </div>
      </header>

      {/* Body */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pb-10">
        {/* Training name */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 max-w-2xl"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-sage-400/80 font-medium mb-4">
            Live Training Session
          </p>
          <h1
            className="text-cream-100 leading-tight"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(22px, 3vw, 38px)',
              fontWeight: 500,
              letterSpacing: '-0.01em',
            }}
          >
            {TRAINING_NAME}
          </h1>
        </motion.div>

        {/* Timer ring */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative flex items-center justify-center mb-10"
        >
          {/* Outer glow */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background: `radial-gradient(circle, ${glowColor} 0%, transparent 65%)`,
              transform: 'scale(1.3)',
            }}
            animate={{
              opacity: running ? [0.4, 0.7, 0.4] : 0.3,
              scale: running ? [1.25, 1.35, 1.25] : 1.3,
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />

          <svg width="300" height="300" className="-rotate-90 relative">
            {/* Track */}
            <circle
              cx="150" cy="150" r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="6"
            />
            {/* Progress ring */}
            <motion.circle
              cx="150" cy="150" r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={circumference}
              animate={{
                strokeDashoffset: circumference * (1 - progress),
                stroke: ringColor,
              }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              style={{ filter: `drop-shadow(0 0 8px ${glowColor})` }}
            />
          </svg>

          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <AnimatePresence mode="wait">
              {finished ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center px-4"
                >
                  <motion.p
                    className="text-cream-100 italic"
                    style={{ fontFamily: 'Georgia, serif', fontSize: 32, fontWeight: 500 }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    Time&apos;s Up
                  </motion.p>
                  <p className="text-sm text-cream-200/50 mt-2 tracking-wide">Break time</p>
                </motion.div>
              ) : (
                <motion.div key="time" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
                  <motion.span
                    className="block font-mono leading-none"
                    style={{
                      fontSize: 56,
                      fontWeight: 300,
                      letterSpacing: '-0.04em',
                      color: isCritical ? '#e74c3c' : isLow ? '#e67e22' : '#F5F2EC',
                    }}
                    animate={isCritical ? { scale: [1, 1.08, 1] } : {}}
                    transition={{ duration: 1, repeat: isCritical ? Infinity : 0, ease: 'easeInOut' }}
                  >
                    {formatTime(remaining)}
                  </motion.span>
                  <span className="block text-xs tracking-[0.25em] uppercase text-cream-200/40 mt-2">
                    {running ? 'in progress' : remaining === total ? 'ready to start' : 'paused'}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Preset pills */}
        <div className="flex items-center gap-2 mb-8 flex-wrap justify-center">
          <span className="text-xs tracking-widest uppercase text-cream-200/40 mr-1">Duration</span>
          {PRESETS.map((p) => {
            const isActive = total === p.seconds;
            return (
              <button
                key={p.label}
                disabled={running}
                onClick={() => handlePreset(p.seconds)}
                className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all"
                style={{
                  background: isActive ? 'rgba(107,155,90,0.25)' : 'rgba(255,255,255,0.05)',
                  color: isActive ? '#B5CBA8' : 'rgba(245,242,236,0.5)',
                  border: isActive ? '1px solid rgba(107,155,90,0.4)' : '1px solid rgba(255,255,255,0.08)',
                  opacity: running ? 0.4 : 1,
                  cursor: running ? 'not-allowed' : 'pointer',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4">
          {!finished && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setRunning(r => !r)}
              className="px-8 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all"
              style={{
                background: running ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #6B9B5A 0%, #558347 100%)',
                color: running ? '#F5F2EC' : '#fff',
                border: running ? '1px solid rgba(255,255,255,0.15)' : 'none',
                minWidth: 130,
                boxShadow: running ? 'none' : '0 4px 20px -3px rgba(107,155,90,0.4)',
              }}
            >
              {running ? 'Pause' : remaining === total ? 'Start' : 'Resume'}
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleReset}
            className="px-8 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all"
            style={{
              background: 'transparent',
              color: 'rgba(245,242,236,0.5)',
              border: '1px solid rgba(255,255,255,0.12)',
              minWidth: 130,
            }}
          >
            Reset
          </motion.button>
        </div>

        {/* Status indicator */}
        <AnimatePresence>
          {running && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-8 flex items-center gap-2"
            >
              <motion.span
                className="w-2 h-2 rounded-full bg-sage-400"
                animate={{ opacity: [1, 0.3, 1], scale: [1, 1.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span className="text-xs tracking-[0.2em] uppercase text-cream-200/40">Counting down</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <footer className="relative z-10 text-center pb-6">
        <p className="text-xs tracking-[0.2em] uppercase text-cream-200/20">
          humanpotentialos.com
        </p>
      </footer>
    </div>
  );
}
